import { Button, Dialer, DropdownButton, Icon } from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import { AsyncStatus } from 'lib-shared-legacy-types';
import { PureComponent } from 'react';

import { buttonize } from 'components/shared/util/buttonize';

import { SoundMeter } from '../soundMeter';

import styles from './phoneBar.module.scss';

type PropsT = {
  /** Extra classnames */
  className?: string;
  /** Sounds */
  sounds: Array<{
    input: number;
    output: number;
    isRecording: boolean;
  }>;
  /** Can the phone call be recorded */
  canRecord: boolean;
  /** Can the agent listen in */
  canListen: boolean;
  /** Is the phone call assigned to the agent */
  isAssigned: boolean;
  /** Is the phone call being recorded */
  isRecording?: boolean;
  /** Is the agent listening in on the phone call */
  isListening?: boolean;
  /** Toggle recording callback */
  toggleRecording?: () => void;
  /** Toggle listening callback */
  toggleListening?: () => void;
  /** The current duration of the phone call (eg. 01:05) */
  duration: string | JSX.Element;
  /** Is the call muted? */
  muted: boolean;
  /** Is the call on hold? */
  onHold: boolean;
  /** Recording asynchronous status */
  recordingAsyncStatus: AsyncStatus;
  /** Mute asynchronous status */
  muteAsyncStatus: AsyncStatus;
  /** Hold asynchronous status */
  holdAsyncStatus: AsyncStatus;
  /** Listen in asynchronous status */
  listenAsyncStatus: AsyncStatus;
  /** On hold toggle callback */
  onHoldToggle: () => void;
  /** On mute toggle callback */
  onMuteToggle: () => void;
  /** On dial callback */
  onDial: (value: string) => void;
};

const noop = () => {
  // do nothing
};

export class PhoneBar extends PureComponent<PropsT> {
  static defaultProps: {
    recordingAsyncStatus: AsyncStatus;
    muteAsyncStatus: AsyncStatus;
    holdAsyncStatus: AsyncStatus;
    listenAsyncStatus: AsyncStatus;
  };

  render() {
    const {
      className,
      sounds,
      canRecord,
      canListen,
      isRecording,
      recordingAsyncStatus,
      muteAsyncStatus,
      holdAsyncStatus,
      toggleRecording = noop,
      toggleListening = noop,
      listenAsyncStatus,
      duration,
      muted,
      onHold,
      onMuteToggle,
      onHoldToggle,
      onDial,
      isAssigned,
      isListening,
      ...otherProps
    } = this.props;

    const rootClass = classnames(
      {
        [styles.phoneBar]: true,
        [styles.phoneBarRecording]: isRecording,
        [styles.phoneBarListening]: isListening,
      },
      className,
    );

    return (
      <div {...otherProps} className={rootClass}>
        <time className={styles.duration}>{duration}</time>
        {canRecord && (
          <div className={styles.recordIcon} {...buttonize(toggleRecording)}>
            <Icon name="rec" />
          </div>
        )}
        {canListen && (
          <div className={styles.listeningIcon} {...buttonize(toggleListening)}>
            <Icon name="headset" />
          </div>
        )}

        <SoundMeter className={styles.meter} sounds={sounds} />
        <div className={styles.actions}>
          {isAssigned && canRecord && isRecording && (
            <Button
              variant="secondary"
              isLoading={recordingAsyncStatus === 'loading'}
              onClick={toggleRecording}
              className={styles.action}
            >
              Pause recording
            </Button>
          )}
          {isAssigned && canRecord && !isRecording && (
            <Button
              variant="danger"
              isLoading={recordingAsyncStatus === 'loading'}
              onClick={toggleRecording}
              className={styles.action}
            >
              Start recording
            </Button>
          )}
          {isAssigned && (
            <DropdownButton
              className={styles.action}
              icon="th"
              dropdown={<Dialer onInput={onDial} />}
            >
              Dial
            </DropdownButton>
          )}
          {(isAssigned || isListening) && (
            <Button
              variant={muted ? 'primary' : 'secondary'}
              isLoading={muteAsyncStatus === 'loading'}
              onClick={onMuteToggle}
              className={styles.action}
            >
              {muted ? 'Unmute' : 'Mute'}
            </Button>
          )}
          {isAssigned && (
            <Button
              variant={onHold ? 'primary' : 'secondary'}
              isLoading={holdAsyncStatus === 'loading'}
              onClick={onHoldToggle}
              className={styles.action}
            >
              {onHold ? 'Unhold' : 'Hold'}
            </Button>
          )}
          {!isAssigned && canListen && (
            <Button
              variant={isListening ? 'secondary' : 'primary'}
              disabled={listenAsyncStatus === 'loading'}
              onClick={toggleListening}
              className={styles.action}
            >
              {isListening ? 'Leave' : 'Listen in'}
            </Button>
          )}
        </div>
      </div>
    );
  }
}
